<script setup lang="ts">
import type { RouteLocationRaw } from '#vue-router'
import { faArrowRight, faBookmark, faClockRotateLeft } from '@fortawesome/pro-light-svg-icons'
import { useLastActivity } from '~/composables/useLastActivity'

const { literatureFavorites, lawFavorites, journalsFavorites, isLoading: isFavoritesLoading } = useFavorites()

const { lastActivity, isLoading: isLastActivityLoading } = useLastActivity()

const isLoading = computed(() => isFavoritesLoading.value || isLastActivityLoading.value)

const totalFavorites = computed(() => literatureFavorites.value.length + lawFavorites.value.length + journalsFavorites.value.length)

const lastActivityRoute = computed<RouteLocationRaw | undefined>(() => {
  if (!lastActivity.value) {
    return undefined
  }

  return { name: lastActivity.value.routeName, params: lastActivity.value.params } as RouteLocationRaw
})
</script>

<template>
  <ul v-if="isLoading" class="hidden md:flex flex-col gap-2xs w-full max-w-xl min-h-[106px]">
    <li class="border-b border-b-moss-100">
      <div class="flex px-s pt-xs pb-m gap-xs items-center">
        <Icon :icon="faClockRotateLeft" class="self-center text-moss-100" />
        <div class="flex-1 flex flex-col gap-3xs">
          <Skeleton class="h-4 w-40 rounded bg-moss-100/20" />
          <Skeleton class="h-5 w-48 rounded bg-moss-100/20" />
        </div>
        <Icon :icon="faArrowRight" class="self-center text-moss-100" />
      </div>
    </li>
    <li>
      <div class="flex px-s pt-xs pb-m gap-xs items-center">
        <Icon :icon="faBookmark" class="self-center text-moss-100" />
        <div class="flex-1 flex flex-col gap-3xs">
          <Skeleton class="h-4 w-56 rounded bg-moss-100/20" />
          <Skeleton class="h-5 w-32 rounded bg-moss-100/20" />
        </div>
        <Icon :icon="faArrowRight" class="self-center text-moss-100" />
      </div>
    </li>
  </ul>

  <ul v-else class="hidden md:flex flex-col gap-2xs w-full max-w-xl min-h-[106px]">
    <li v-if="lastActivityRoute && lastActivity?.title">
      <OverviewShortcutItem
        id="last-activity-link"
        :icon-left="faClockRotateLeft"
        :icon-right="faArrowRight"
        label="Fortsett der du slapp"
        :text="lastActivity.title"
        :to="lastActivityRoute"
      />
    </li>
    <li>
      <OverviewShortcutItem
        id="all-favorites-link"
        :icon-left="faBookmark"
        :icon-right="faArrowRight"
        label="Se alle dine favorittmarkeringer"
        :text="`${totalFavorites} favoritter`"
        :to="{ name: 'workspace-favoritter' }"
      />
    </li>
  </ul>
</template>
